import Badge from 'LuxKit/Badge'
import LineEnvelopeIcon from 'LuxKit/Icons/line/LineEnvelopeIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LineSmileIcon from 'LuxKit/Icons/line/LineSmileIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import NavBarListItem from 'components/NavKit/NavBarListItem'
import config from 'constants/config'
import { FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG } from 'constants/friendsAndFamilyDiscount'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { useCallback, useContext, useMemo } from 'react'
import loadable from '@loadable/component'
import { useIntl } from 'react-intl'
import {
  hasIncompleteInformation as getHasIncompleteInformation,
  hasUserPreferencesNotification as getHasUserPreferencesNotification,
  isLEStaff,
} from 'selectors/accountSelectors'
import NavBarListItemDivider from '../NavBarListItemDivider'
import NavBarListItemIcon from '../NavBarListItemIcon'
import AccountMenuCreditListItem from './AccountMenuCreditListItem'
import AccountMenuDrawerHeader from './AccountMenuDrawerHeader'
import AccountMenuLogoutListItem from './AccountMenuLogoutListItem'
import AccountMenuLuxPlusDetailsTile from './AccountMenuLuxPlusDetailsTile'
import AccountMenuMyEscapesListItem from './AccountMenuMyEscapesListItem'
import AccountMenuShopOrdersListItem from './AccountMenuShopOrdersListItem'
import AccountMenuAITripPlannerListItem from './AccountMenuAITripPlannerListItem'
import AccountMenuTripPlannerListItem from './AccountMenuTripPlannerListItem'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import ModalContext from 'contexts/ModalContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AccountMenuLoyaltyTiles from './AccountMenuLoyaltyTiles'
import StatusMatchChallengeCard from 'loyalty/pages/statusMatch/StatusMatchChallengeCard'
import Sheet from 'components/utils/Sheet'
import LineCogIcon from 'LuxKit/Icons/line/LineCogIcon'
import { DevPanelOpenContext } from 'components/App/DevPanel/DevPanelContext'
import LineCalendarHeartIcon from 'LuxKit/Icons/line/LineCalendarHeartIcon'
import useMyWeddings from 'weddings/hooks/useMyWeddings'
import useNavigationVouchersCount from 'marketing/hooks/useNavigationVouchersCount'

const ExperimentControlPanel = loadable(() => import('components/DevTools/ExperimentControlPanel/ExperimentControlPanel'))

function AccountMenuDrawer() {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const handleLinkItemClick = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])
  const isStoreMode = useAppSelector(state => state.system.storeMode)
  const referralProgramEnabled = useAppSelector(state => state.auth.account.referralProgramEnabled)
  const hasIncompleteInformation = useAppSelector(getHasIncompleteInformation)
  const hasUserPreferencesNotification = useAppSelector(getHasUserPreferencesNotification)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isStaff = useAppSelector(isLEStaff)
  const isFriendsAndFamilyPageEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterFnfPage)
  const canSeeExperimentPanel = isSpoofed || isStaff
  const isMerchandiseEnabled = useAppSelector(getIsMerchandiseEnabled)
  const { count: vouchersCount, isLoading: isLoadingActiveVouchers } = useNavigationVouchersCount({
    enabled: config.ENABLE_VOUCHER_PAGE && !!memberId,
  })
  const showModal = useContext(ModalContext)

  const { data: weddings } = useMyWeddings({ enabled: config.WEDDINGS_ENABLED && !!memberId })

  const openDevPanel = useContext(DevPanelOpenContext)

  const showExperimentPanel = useCallback(() => {
    showModal(<ExperimentControlPanel />)
  }, [showModal])

  const handleOpenDevPanel = useCallback(() => {
    modalElement.resolve()
    setTimeout(openDevPanel, 100)
  }, [modalElement, openDevPanel])

  const vouchersNavTitle = useMemo(() => {
    const showActiveVouchersCount =
      !isLoadingActiveVouchers && vouchersCount > 0
    if (!showActiveVouchersCount) {
      return intl.formatMessage({
        id: '6T8OVae9wL3GiyQSsDWX',
        defaultMessage: 'Vouchers',
        description:
          'Account menu navigation label for the vouchers page when the active-voucher list is not shown, including while loading, before any fetch, or when the last loaded list was not active vouchers (e.g. redeemed or expired tab)',
      })
    }
    return intl.formatMessage(
      {
        id: 'wXW8ZV8-OsjNwy1emsv3',
        defaultMessage: 'Vouchers ({count})',
        description:
          'Account menu navigation label for the vouchers page, including the count of vouchers currently stored in application state from the last vouchers fetch.',
      },
      { count: vouchersCount },
    )
  }, [intl, isLoadingActiveVouchers, vouchersCount])

  return <ModalBase mode="drawer-starboard">
    <AccountMenuDrawerHeader />
    <ModalBody>
      <ModalContent flush>
        <VerticalSpacer gap={20}>
          <Sheet padding="0 20">
            <VerticalSpacer gap={16}>
              <AccountMenuLoyaltyTiles onLinkClick={handleLinkItemClick} />
              <StatusMatchChallengeCard variant="drawer" onLinkClick={handleLinkItemClick} />
              <AccountMenuLuxPlusDetailsTile onLinkClick={handleLinkItemClick} />
            </VerticalSpacer>
          </Sheet>
          <VerticalSpacer gap={8}>
            <AccountMenuMyEscapesListItem onClick={handleLinkItemClick} />
            {!!weddings?.length && <NavBarListItem
              startIcon={<NavBarListItemIcon Icon={LineCalendarHeartIcon} />}
              title={intl.formatMessage({
                id: 'cIrzVuUZUrG2LmOvHJEC',
                defaultMessage: 'Our Wedding',
                description: 'Navigation item title for the wedding setup page in the account menu',
              })}
              to={`/weddings/${weddings[0].slug}/edit`}
              onClick={handleLinkItemClick}
            />}
            {isMerchandiseEnabled && <AccountMenuShopOrdersListItem onClick={handleLinkItemClick} />}
            {!isStoreMode && <AccountMenuTripPlannerListItem onClick={handleLinkItemClick} />}
            {!isStoreMode && <AccountMenuAITripPlannerListItem onClick={handleLinkItemClick} />}
            <NavBarListItemDivider />
            <NavBarListItem
              startIcon={<NavBarListItemIcon Icon={LineUserIcon} />}
              title={intl.formatMessage({
                id: 'U80-J3KDXei79Aqe5edq',
                defaultMessage: 'My profile',
                description: 'Navigation item title for account profile page in the navigation bar',
              })}
              to="/account/details"
              onClick={handleLinkItemClick}
              endIcon={hasIncompleteInformation ? <Badge kind="dot" mode="critical" /> : undefined}
            />
            {isStaff && isFriendsAndFamilyPageEnabled && <NavBarListItem
              startIcon={<NavBarListItemIcon Icon={LineSmileIcon} />}
              title={intl.formatMessage({
                id: '2-JDZjoaa9jk1jC5GZOE',
                defaultMessage: 'Friends & Family Discount',
                description: 'Navigation item title for the friends and family discount invite page in the account menu',
              })}
              to={`/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`}
              onClick={handleLinkItemClick}
            />}
            {!isStoreMode && <>
              <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineUsersAltIcon} />}
                title={intl.formatMessage({
                  id: 'tj_ykm_9tToLVsrMWQxW',
                  defaultMessage: 'Travel companions',
                  description: 'Navigation item title for travel companions page in the navigation bar',
                })}
                to="/account/travel-companions"
                onClick={handleLinkItemClick}
              />
              {config.USER_PREFERENCES_ENABLED && <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineGlobeAltIcon} />}
                title={intl.formatMessage({
                  id: 'Xm5eCVHB_8mMSo809-Xk',
                  defaultMessage: 'Travel preferences',
                  description: 'Navigation item title for travel preferences page in the navigation bar',
                })}
                to="/account/travel-preferences"
                onClick={handleLinkItemClick}
                endIcon={hasUserPreferencesNotification ? <Badge kind="dot" mode="critical" /> : undefined}
              />}
              {config.hasSubscriptions && <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineEnvelopeIcon} />}
                title={intl.formatMessage({
                  id: 'OA0AfpHHfMXjUI8j5YcS',
                  defaultMessage: 'Communication preferences',
                  description: 'Navigation item title for communication preferences page',
                })}
                to="/account/communication-preferences"
                onClick={handleLinkItemClick}
              />}
              <AccountMenuCreditListItem onClick={handleLinkItemClick} />
            </>}
            {!isStoreMode && config.ENABLE_VOUCHER_PAGE && <NavBarListItem
              startIcon={<NavBarListItemIcon Icon={LineTicketIcon} />}
              title={vouchersNavTitle}
              to="/account/vouchers"
              onClick={handleLinkItemClick}
            />}
            {!isStoreMode && config.hasReferralProgram && referralProgramEnabled && <>
              <NavBarListItemDivider />
              <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineGiftIcon} />}
                title={intl.formatMessage({
                  id: 'B3lOCQ8ixLEM3YFRZSR6',
                  defaultMessage: 'Refer a friend',
                  description: 'Navigation item title for refer a friend page in the navigation bar',
                })}
                to="/refer"
                onClick={handleLinkItemClick}
              />
            </>}
            {canSeeExperimentPanel && <NavBarListItem
              title={intl.formatMessage({
                id: 'QQdpJbIYUv8XvSYvGDbF',
                defaultMessage: 'Show Experiment Panel',
                description: 'Navigation item title for showing experiment control panel',
              })}
              onClick={showExperimentPanel}
            />}
            {config.ENABLE_DEV_PANEL && <>
              <NavBarListItemDivider />
              <NavBarListItem
                startIcon={<NavBarListItemIcon Icon={LineCogIcon} />}
                title={intl.formatMessage({
                  id: 'z4obgz-lW9LZ2dv6x-1-',
                  defaultMessage: 'Dev Tools',
                  description: 'Navigation item title for opening the customer portal developer tools panel',
                })}
                onClick={handleOpenDevPanel}
              />
            </>}
            <NavBarListItemDivider />
            <AccountMenuLogoutListItem onClick={handleLinkItemClick} />
          </VerticalSpacer>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default AccountMenuDrawer
